import React, { useState } from "react";
import {
  Navbar,
  NavbarBrand,
  Nav,
  NavItem,
  NavLink,
  NavbarToggler,
  Collapse,
  Button
} from "reactstrap";

const MyNavbar = ({ userData, navigate, toggleModal, showNavbar }) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggle = () => setIsOpen(!isOpen);

  return (
    <>
      {/* Injecting modern Wix/Hostinger style overrides directly */}
      <style>{`
        :root {
          --nav-bg: rgba(255, 255, 255, 0.85);
          --nav-border: rgba(0, 0, 0, 0.06);
          --text-main: #111827;
          --text-muted: #4b5563;
          --brand-primary: #6366f1; /* Premium Hostinger-style Indigo */
          --transition-smooth: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        }

        /* Navbar Layout & Animations */
        .modern-navbar {
          background-color: var(--nav-bg) !important;
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border-bottom: 1px solid var(--nav-border);
          transition: transform 0.3s ease, opacity 0.3s ease;
          box-shadow: 0 4px 30px rgba(0, 0, 0, 0.02);
          z-index: 1050;
        }
        
        .navbar-hide {
          transform: translateY(-100%);
          opacity: 0;
        }
        
        .navbar-show {
          transform: translateY(0);
          opacity: 1;
        }

        /* Brand / Logo Styling */
        .modern-brand {
          font-weight: 700 !important;
          font-size: 1.25rem !important;
          color: var(--text-main) !important;
          letter-spacing: -0.5px;
          transition: var(--transition-smooth);
        }
        .modern-brand:hover {
          opacity: 0.85;
        }
        .brand-icon {
          font-size: 1.4rem;
        }

        /* Navigation Links */
        .modern-nav-link {
          color: var(--text-muted) !important;
          font-size: 0.95rem !important;
          font-weight: 500 !important;
          padding: 0.5rem 1rem !important;
          transition: var(--transition-smooth);
          position: relative;
        }
        .modern-nav-link:hover {
          color: var(--brand-primary) !important;
        }
        
        /* Premium Hover underline effect for Desktop */
        @media (min-width: 768px) {
          .modern-nav-link::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 50%;
            width: 0;
            height: 2px;
            background-color: var(--brand-primary);
            transition: var(--transition-smooth);
            transform: translateX(-50%);
          }
          .modern-nav-link:hover::after {
            width: 60%;
          }
        }

        /* Custom Toggler styling */
        .modern-toggler {
          border: none !important;
          outline: none !important;
          padding: 0.25rem !important;
        }
        .modern-toggler:focus {
          box-shadow: none !important;
        }

        /* Modern CTA Buttons */
        .cta-btn-desktop {
          background-color: var(--text-main) !important;
          color: #ffffff !important;
          font-weight: 600 !important;
          font-size: 0.9rem !important;
          padding: 0.6rem 1.25rem !important;
          border-radius: 8px !important;
          border: 1px solid var(--text-main) !important;
          transition: var(--transition-smooth);
        }
        .cta-btn-desktop:hover {
          background-color: var(--brand-primary) !important;
          border-color: var(--brand-primary) !important;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.2);
        }

        .cta-btn-mobile {
          background-color: transparent !important;
          color: var(--text-main) !important;
          font-weight: 600 !important;
          font-size: 0.85rem !important;
          border: 1px solid rgba(0, 0, 0, 0.15) !important;
          border-radius: 6px !important;
          padding: 0.4rem 0.85rem !important;
          transition: var(--transition-smooth);
        }
        .cta-btn-mobile:active {
          background-color: rgba(0, 0, 0, 0.05) !important;
        }

        /* Mobile Dropdown Styling Fixes */
        @media (max-width: 767.98px) {
          .navbar-collapse {
            background: #ffffff;
            margin-top: 0.75rem;
            border-radius: 12px;
            box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
            border: 1px solid var(--nav-border);
            overflow: hidden;
          }
          .modern-nav-link {
            padding: 0.75rem 1.2rem !important;
          }
          .modern-nav-link:active {
            background-color: rgba(99, 102, 241, 0.05);
          }
        }
      `}</style>

      <Navbar
        expand="md"
        fixed="top"
        className={`modern-navbar px-3 px-md-5 py-2 py-md-3 ${
          showNavbar ? "navbar-show" : "navbar-hide"
        }`}
      >
        {/* BRAND / LOGO */}
        <NavbarBrand
          href="/"
          className="d-flex align-items-center gap-2 modern-brand me-auto me-md-0"
        >
          <span className="brand-icon">Kyts</span>
          <span>Library</span>
        </NavbarBrand>

        {/* MOBILE ONLY ACTIONS */}
        <div className="d-flex align-items-center gap-2 d-md-none">
          <Button 
            onClick={toggleModal} 
            className="cta-btn-mobile"
          >
            Login
          </Button>
          <NavbarToggler onClick={toggle} className="modern-toggler" />
        </div>

        {/* COLLAPSIBLE MENU */}
        <Collapse isOpen={isOpen} navbar>
          <Nav className="mx-auto text-start text-md-center py-2 py-md-0" navbar>
            {[
              ["Home", "#home"],
              ["How it Works", "#how-it-works"],
              ["Features", "#features"],
              ["Pricing", "#pricing"],
            ].map(([label, href]) => (
              <NavItem key={label}>
                <NavLink href={href} className="modern-nav-link" onClick={() => setIsOpen(false)}>
                  {label}
                </NavLink>
              </NavItem>
            ))}

            <NavItem>
              <NavLink
                href="#"
                onClick={() => { navigate("/books"); setIsOpen(false); }}
                className="modern-nav-link"
              >
                Catalog
              </NavLink>
            </NavItem>

            {userData?.rights === "Admin" && (
              <NavItem>
                <NavLink
                  href="#"
                  onClick={() => { navigate("/admin/dashboard"); setIsOpen(false); }}
                  className="modern-nav-link"
                >
                  Dashboard
                </NavLink>
              </NavItem>
            )}
          </Nav>

          {/* DESKTOP ONLY ACTIONS */}
          <Nav className="ms-auto d-none d-md-flex align-items-center" navbar>
            <NavItem>
              <Button
                onClick={toggleModal}
                className="cta-btn-desktop"
              >
                Login / Sign Up
              </Button>
            </NavItem>
          </Nav>
        </Collapse>
      </Navbar>
    </>
  );
};

export default MyNavbar;